import * as Sentry from '@sentry/react';
import { createMask } from 'imask';
import { declOfNum } from '@/libs/helpers/index';
import { BreadcrumbCategories, ErrorCustomParametres, ErrorSourceNames, } from '../types/global';
import { CURRENCIES_BADGES, DENIED_MESSAGES_FOR_BREADCRUMBS, DENIED_URLS_FOR_BREADCRUMBS, IS_DEVELOPMENT, } from '../constants';
import { getTypes } from '../static';
export const filterBreadcrumbs = (breadcrumb) => {
    const { category, message, data } = breadcrumb;
    const breadcrumbCategories = Object.values(BreadcrumbCategories);
    if (category && breadcrumbCategories.includes(category)) {
        const isXhrCategoryDeniedBreadcrumb = category === BreadcrumbCategories.XHR &&
            DENIED_URLS_FOR_BREADCRUMBS.some((deniedUrl) => data?.url.includes(deniedUrl));
        const isConsoleCategoryDeniedBreadcrumb = category === BreadcrumbCategories.CONSOLE &&
            DENIED_MESSAGES_FOR_BREADCRUMBS.some((deniedMessage) => message?.includes(deniedMessage));
        if (isXhrCategoryDeniedBreadcrumb ||
            isConsoleCategoryDeniedBreadcrumb) {
            return null;
        }
    }
    return breadcrumb;
};
export const initializeErrorReporter = (SENTRY_CONFIG) => {
    const { dsn, environment } = SENTRY_CONFIG;
    return Sentry.init({
        dsn,
        environment,
        debug: IS_DEVELOPMENT,
        beforeBreadcrumb(breadcrumb) {
            return filterBreadcrumbs(breadcrumb);
        },
    });
};
export const captureError = (error, info) => {
    let eventId = '';
    Sentry.withScope((scope) => {
        scope.setTags({
            [ErrorCustomParametres.SOURCE]: ErrorSourceNames.CLIENT,
            ...(typeof info === 'string' && { context: info }),
        });
        eventId = Sentry.captureException(error);
        if (info) {
            scope.setExtra('info', JSON.stringify(info, null, '\t'));
        }
    });
    return eventId;
};
export const createMaskedValue = (value, maskOptions) => {
    const inputMask = createMask({ ...maskOptions });
    return inputMask.resolve(value);
};
export const separatePriceBySpaces = (value) => value
    .toString()
    .replace(/\s/g, '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
export const formatPrice = (price) => separatePriceBySpaces(Math.ceil(Number(price)));
export const formatPriceWithCurrency = (price, currency) => {
    const formattedPrice = formatPrice(price);
    return `${formattedPrice} ${CURRENCIES_BADGES[currency]}`;
};
export const getLabelDeclination = (quantity, words) => `${quantity} ${declOfNum(quantity, words)}`;
export const getCountriesLabel = (countriesQuantity) => getLabelDeclination(countriesQuantity, getTypes('COUNTRY'));
export const getDaysLabel = (daysQuantity) => getLabelDeclination(daysQuantity, getTypes('DAY'));
export const getCardOptoinsLabel = (optionsQuantity) => getLabelDeclination(optionsQuantity, getTypes('SERVICESMALL'));
export const getAgeLabel = (age) => {
    if (!age) {
        return 'до 1 года';
    }
    return `${age} ${declOfNum(age, getTypes('AGE'))}`;
};
export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
};
export const getPassengersLabelValue = (selectedPassengers = []) => {
    const transformAge = (age, isLast) => isLast || age === 0 ? getAgeLabel(age).toLowerCase() : age;
    return selectedPassengers
        .map(({ age }, index) => transformAge(age, index === selectedPassengers.length - 1))
        .join(', ');
};
export const addToArrayIfNotExist = (array, item) => {
    if (!array.includes(item)) {
        return [...array, item];
    }
    return array;
};
export const removeFromArray = (array, removable) => {
    const multilpleRemove = Array.isArray(removable);
    return array.filter((elem) => {
        if (multilpleRemove) {
            return !removable.includes(elem);
        }
        else {
            return elem !== removable;
        }
    });
};
export const getKeys = (target) => Object.keys(target);
export const getValues = (target) => Object.values(target);
export const getEntries = (target) => Object.entries(target);
export const getObjectFilledWithValue = (fields, value) => getKeys(fields).reduce((object, field) => ((object[field] = value), object), {});
export const filterDuplicates = (array) => {
    const seen = new Set();
    return array.filter((item) => {
        if (seen.has(item)) {
            return false;
        }
        seen.add(item);
        return true;
    });
};
